import React, { useState } from 'react';
import {
  Card,
  Box,
  makeStyles,
  Typography,
  CardContent,
  Slider,
  withStyles,
  Divider,
} from '@material-ui/core';
import { LISTS } from 'modules/student/school';
import NotesIcon from '@material-ui/icons/Notes';
import { platforms } from '../constants';
import ADMISSION_TYPES from 'constant/admission-types';
import Dropdown from 'components/form/Dropdown';
import STATUSES from 'constant/college-application-status';
import { Link } from 'react-router-dom';
import MuLink from '@material-ui/core/Link';
import DatePickerLabel from 'components/common/DatePickerLabel';
import { useMutation } from 'react-apollo';
import { UPDATE_SCHOOL } from 'graphql/student_college';
import LocationOnIcon from '@material-ui/icons/LocationOn';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    borderRadius: 6,
  },
  header: {
    '& > div': {
      flexShrink: 0,
    }
  },
  categoryLabel: {
    fontSize: 12,
    flex: 3,
    textTransform: 'uppercase',
  },
  icon: {
    color: theme.palette.text.secondary,
    paddingRight: theme.spacing(0.5),
  },
  noteicon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  label: {
    paddingBottom: theme.spacing(1),
    textTransform: 'uppercase',
    fontWeight: 700,
    fontSize: 10,
    color: theme.palette.text.secondary,
    letterSpacing: 1,
  },
  content: {
    padding: theme.spacing(3),
  },
}));

export const ProgressSlider = withStyles({
  root: {
    color: '#25C186',
    height: 8,
    width: 108,
  },
  thumb: {
    'height': 16,
    'width': 16,
    'backgroundColor': '#fff',
    'border': '2px solid currentColor',
    'marginTop': -5,
    'marginLeft': -12,
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 4px)',
  },
  track: {
    height: 4,
    borderRadius: 4,
  },
  rail: {
    height: 4,
    borderRadius: 4,
  },
})(Slider);

const CardInfo = (props) => {
  const classes = useStyles();
  const {
    data,
    student,
    user,
    onStatusChange,
    onAdmissionTypeChange,
    onPlatformChange,
    onProgressChange,
  } = props;
  const canEdit = user.type !== 'parent';
  const Icon = LISTS.find((f) => f.type === data?.category)?.Icon;
  const color = LISTS.find((f) => f.type === data?.category)?.color;
  const name = LISTS.find((f) => f.type === data?.category)?.name;
  const [updateSchool] = useMutation(UPDATE_SCHOOL);

  const onDateChange = (date) => {
    updateSchool({
      variables: {
        id: data.id,
        input: {
          app_deadline: date.format('YYYY-MM-DD'),
        },
      },
    });
  };

  return (
    <Card className={classes.root}>
      <CardContent className={classes.content}>
        <Box>
          <Box
            display="flex"
            alignItems="center"
            marginBottom={1}
            className={classes.header}
          >
            {Icon && (
              <Icon className={classes.icon} style={{ color, opacity: 0.6 }} />
            )}
            <Typography
              variant="body2"
              className={classes.categoryLabel}
              color="textSecondary"
            >
              {name}
            </Typography>
            <DatePickerLabel
              value={data.app_deadline}
              onChange={onDateChange}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            marginRight={10}
            flexWrap="wrap"
          >
            <Typography variant="h6">
              <strong>
                {canEdit ? (
                  <MuLink component={Link} to={`tracker/${data?.college_id}`}>
                    {data?.college_name}
                  </MuLink>
                ) : (
                  data?.college_name
                )}
              </strong>
            </Typography>
            {data?.tracker_info?.notes && (
              <Box fontStyle="italic" display="flex">
                <NotesIcon className={classes.noteicon} />
                <Typography variant="body2" style={{ color: '#AAB5CE' }}>
                  {data?.tracker_info?.notes}
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" marginBottom={3}>
            {data.college_location && (
              <>
                {' '}
                <LocationOnIcon fontSize="small" className={classes.icon} />
                <Typography gutterBottom color="textSecondary">
                  {data.college_location}
                </Typography>
              </>
            )}
          </Box>
          <Box marginBottom={4} display="flex" justifyContent="space-between"  flexWrap="wrap">
            <Box>
              <Typography className={classes.label}>Status</Typography>
              <Dropdown
                disabled={!canEdit}
                options={STATUSES}
                id="status"
                value={data.status}
                onChange={(val) => onStatusChange(val, data)}
                color={STATUSES.find((s) => s.value === data.status)?.color}
              />
            </Box>
            <Box>
              <Typography className={classes.label}>Admission Type</Typography>
              <Dropdown
                showValue
                disabled={!canEdit}
                id="admission_type"
                value={data?.admission_type}
                onChange={(e) => onAdmissionTypeChange(e, data)}
                options={ADMISSION_TYPES}
              />
            </Box>
            <Box flexGrow={{ xs: 2, sm: 0 }} marginTop={{ xs: 2, sm: 0 }}>
              <Typography className={classes.label}>
                Application Platform
              </Typography>
              <Dropdown
                options={platforms}
                disabled={!canEdit}
                id="app_platform"
                value={data?.app_platform}
                onChange={(e) => onPlatformChange(e, data)}
              />
            </Box>
          </Box>
          <Divider />
          <Box
            marginTop={4}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography className={classes.label}>Progress</Typography>
            <ProgressSlider
              disabled={!canEdit}
              aria-label="progress slider"
              defaultValue={data.progress}
              onChangeCommitted={(e, value) => onProgressChange(e, value, data)}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CardInfo;
